import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import type { SpecItem, ClientMeta, GlobalConfig, CatalogItem, BeforeInstallPromptEvent, KitchenUnit, ProjectMeta, StoreLocation } from '../types';
import { PricingRow, calculatePricing, MAX_MANUAL_ROWS } from '../lib/pricing';
import { resolveConfigNames, collectBodyColors, syncBodyColorSpecItems, sortVisualAttachmentItems, buildPreviewPricing } from '../lib/proposalDerived';
import { KITCHEN_COMPATIBILITY_RULES_DEFAULT, STORE_LOCATIONS, BODY_COLORS } from '../constants';
import { formatPhoneNumber, slugifyStoreName } from '../utils';
import catalogData from '../catalog.json';
import * as XLSX from 'xlsx';
import { extractExcelImages } from '../utils/excelImages';
import { parseXmlProject, ParsedXmlProject, findCatalogColorItem } from '../utils/xmlParser';
import { compressImageFile, compressBase64Image } from '../utils/imageUtils';
import { buildColorCatalogItem } from '../utils/colorCatalog';
import { db, auth } from '../lib/firebase';
import { collection, doc, setDoc, writeBatch, onSnapshot, getDocs, increment } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import type { User } from 'firebase/auth';

const STANDARD_CATEGORIES = ['Aksesuar', 'Aydınlatma', 'Kulp', 'Tezgâh', 'Renk', 'Gövde Rengi', 'Cihaz', 'Eviye', 'Armatür'];

export const trLower = (str: any) => String(str || '').trim()
  .replace(/İ/g,'i').replace(/I/g,'ı').replace(/Ş/g,'ş').replace(/Ç/g,'ç')
  .replace(/Ğ/g,'ğ').replace(/Ü/g,'ü').replace(/Ö/g,'ö').replace(/â/g,'a').replace(/î/g,'i').toLowerCase();

export const normalizeCategory = (raw: string, fallback = 'Aksesuar'): string => {
  if (!raw) return fallback;
  const c = trLower(raw);

  if (c.includes('aksesuar') || c.includes('raf') || c.includes('modul') || c.includes('supurgelik') || c.includes('panel') || c.includes('goz') || c.includes('organizer') || c.includes('cekmece') || c.includes('mekanizma') || c.includes('ray')) return 'Aksesuar';
  if (c.includes('aydinlatma') || c.includes('led') || c.includes('isik') || c.includes('spot') || c.includes('trafo')) return 'Aydınlatma';
  if (c.includes('kulp') || c.includes('handle') || c.includes('gola') || c.includes('profil')) return 'Kulp';
  if (c.includes('tezgah') || c.includes('tezgâh') || c.includes('granit') || c.includes('mermer') || c.includes('kuvars') || c.includes('dekton') || c.includes('silestone') || c.includes('corian') || c.includes('ahsap') || c.includes('laminat')) return 'Tezgâh';
  if (c.includes('gövde') || c.includes('govde')) return 'Gövde Rengi';
  if (c.includes('renk') || c.includes('kapak') || c.includes('lake') || c.includes('akrilik') || c.includes('membran') || c.includes('suntalam') || c.includes('mdflam')) return 'Renk';
  if (c.includes('cihaz') || c.includes('ankastre') || c.includes('firin') || c.includes('ocak') || c.includes('davlumbaz') || c.includes('aspirator') || c.includes('esya') || c.includes('makine') || c.includes('kahve') || c.includes('kettle') || c.includes('buzdolabi') || c.includes('mikrodalga') || c.includes('smeg') || c.includes('siemens') || c.includes('bosch') || c.includes('gaggenau') || c.includes('miele') || c.includes('franke') || c.includes('blanco')) return 'Cihaz';
  if (c.includes('eviye') || c.includes('evye') || c.includes('sink')) return 'Eviye';
  if (c.includes('armatur') || c.includes('armatür') || c.includes('batarya') || c.includes('musluk') || c.includes('tap') || c.includes('faucet')) return 'Armatür';

  const match = STANDARD_CATEGORIES.find(cat => trLower(cat) === c);
  if (match) return match;

  return STANDARD_CATEGORIES.includes(fallback) ? fallback : 'Aksesuar';
};

// ─── Default Values ─────────────────────────────────────────────────────────────

export const DEFAULT_PRICING_ROWS: PricingRow[] = [
  { key: 'model_cabinets',       categoryName: 'Mutfak Dolapları',   listPrice: '', campaignDiscount: '',   extraDiscount: '', vatRate: 0.10 },
  { key: 'internal_accessories', categoryName: 'Dahili Aksesuarlar', listPrice: '', campaignDiscount: '',   extraDiscount: '', vatRate: 0.10 },
  { key: 'external_accessories', categoryName: 'Harici Aksesuarlar', listPrice: '', campaignDiscount: '',   extraDiscount: '', vatRate: 0.20 },
  { key: 'shipping_assembly',    categoryName: 'Nakliye & Montaj',   listPrice: '', campaignDiscount: '',   extraDiscount: '', vatRate: 0.20 },
  { key: 'countertop',           categoryName: 'Tezgâh',             listPrice: '', campaignDiscount: '',   extraDiscount: '', vatRate: 0.20 },
  { key: 'appliances',           categoryName: 'Cihaz',              listPrice: '', campaignDiscount: '',   extraDiscount: '', vatRate: 0.20 },
  { key: 'sinks',                categoryName: 'Eviye',              listPrice: '', campaignDiscount: '',   extraDiscount: '', vatRate: 0.20 },
  { key: 'taps',                 categoryName: 'Armatür',            listPrice: '', campaignDiscount: '',   extraDiscount: '', vatRate: 0.20 },
];

const DEFAULT_CLIENT: ClientMeta = {
  clientName: '', phone: '', address: '', designer: '',
  date: '', validityDate: '', crmNo: '', storeName: '', designerPhone: '',
};

const DEFAULT_CONFIG: GlobalConfig = {
  model: '', customModel: '', extraModels: [],
  material: '', customMaterial: '', extraMaterials: [],
  doorColor: '', customDoorColor: '', extraDoorColors: [],
  bodyColor: '', extraBodyColors: [], plinthHeight: 'Baza 12 cm', glassType: '', extraGlassTypes: [], profileType: '', profileColor: '', extraProfileColors: [], leadTime: '',
};

// ─── Context Shape ───────────────────────────────────────────────────────────────

export interface AppContextValue {
  // UI mode
  mode: 'welcome' | 'edit' | 'preview' | 'users' | 'analytics' | 'salesPoints';
  setMode: (m: 'welcome' | 'edit' | 'preview' | 'users' | 'analytics' | 'salesPoints') => void;
  editTab: 'client' | 'config' | 'pricing' | 'specs' | 'all';
  setEditTab: (tab: 'client' | 'config' | 'pricing' | 'specs' | 'all') => void;

  // Refs
  fileInputRef: React.RefObject<HTMLInputElement>;
  jsonInputRef: React.RefObject<HTMLInputElement>;
  xmlInputRef: React.RefObject<HTMLInputElement>;
  projectImageInputRef: React.RefObject<HTMLInputElement>;
  catalogImportRef: React.RefObject<HTMLInputElement>;
  excelImportRef: React.RefObject<HTMLInputElement>;
  isUploadingExcel: boolean;
  handleDownloadExcelTemplate: () => void;
  fileMenuRef: React.RefObject<HTMLDivElement>;

  // File menu
  fileMenuOpen: boolean;
  setFileMenuOpen: (v: boolean) => void;

  // Upload tracking
  uploadActiveItemId: string | null;

  // Catalog data & admin
  catalog: CatalogItem[];
  isAdminMode: boolean;
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isCatalogModalOpen: boolean;
  setIsCatalogModalOpen: (v: boolean) => void;
  catalogSearch: string;
  setCatalogSearch: (v: string) => void;
  selectedCatalogCategory: string;
  setSelectedCatalogCategory: (v: string) => void;
  catalogActiveTab: 'products' | 'matrix';
  setCatalogActiveTab: (v: 'products' | 'matrix') => void;
  isEditingCatalogItem: boolean;
  setIsEditingCatalogItem: (v: boolean) => void;
  editingItemCode: string | null;
  setEditingItemCode: (v: string | null) => void;
  catalogForm: { category: string; code: string; name: string; image: string | null; imageRemoved?: boolean };
  setCatalogForm: React.Dispatch<React.SetStateAction<{ category: string; code: string; name: string; image: string | null; imageRemoved?: boolean }>>;

  // Compatibility rules & matrix editor
  compatibilityRules: Record<string, Record<string, string[]>>;
  selectedMatrixModel: string;
  setSelectedMatrixModel: (v: string) => void;
  selectedMatrixMaterial: string;
  setSelectedMatrixMaterial: (v: string) => void;
  newMatrixModelName: string;
  setNewMatrixModelName: (v: string) => void;
  newMatrixMaterialName: string;
  setNewMatrixMaterialName: (v: string) => void;
  newMatrixColorName: string;
  setNewMatrixColorName: (v: string) => void;

  // PWA
  deferredPrompt: BeforeInstallPromptEvent | null;
  isStandalone: boolean;
  isBannerDismissed: boolean;
  showIOSGuide: boolean;
  setShowIOSGuide: (v: boolean) => void;
  pwaInstalled: boolean;
  isIOS: boolean;
  isSafari: boolean;

  // Core proposal data
  client: ClientMeta;
  setClient: React.Dispatch<React.SetStateAction<ClientMeta>>;
  config: GlobalConfig;
  setConfig: React.Dispatch<React.SetStateAction<GlobalConfig>>;
  pricingRows: PricingRow[];
  setPricingRows: React.Dispatch<React.SetStateAction<PricingRow[]>>;
  /** Satış noktasının küsuratlı çıkan teklifleri yuvarlamak için girdiği KDV dahil TL tutarı;
   *  Genel Toplam'dan doğrudan düşülür. Yalnızca retail teklifte kullanılıyor (toplu projede henüz yok). */
  salesPointDiscount: number;
  setSalesPointDiscount: React.Dispatch<React.SetStateAction<number>>;
  specItems: SpecItem[];
  setSpecItems: React.Dispatch<React.SetStateAction<SpecItem[]>>;
  projectImages: string[];
  setProjectImages: React.Dispatch<React.SetStateAction<string[]>>;
  draggedIndex: number | null;
  isDragging: boolean;
  setIsDragging: (v: boolean) => void;

  // Bulk / project proposal (Toplu Proje Teklifi)
  proposalType: 'retail' | 'bulk';
  projectMeta: ProjectMeta;
  setProjectMeta: React.Dispatch<React.SetStateAction<ProjectMeta>>;
  units: KitchenUnit[];
  activeUnitId: string;
  setActiveUnitId: (id: string) => void;
  addUnit: () => void;
  removeUnit: (id: string) => void;
  renameUnit: (id: string, label: string) => void;
  setUnitQuantity: (id: string, quantity: number) => void;
  setUnitManualPricing: (id: string, fields: Partial<Pick<KitchenUnit, 'manualUnitPrice' | 'manualVat'>>) => void;

  // Spec search
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  showSearchResults: boolean;
  setShowSearchResults: (v: boolean) => void;
  activeSpecCategory: string;
  setActiveSpecCategory: (v: string) => void;

  // Derived / computed
  MODELS: string[];
  availableMaterials: string[];
  getAvailableMaterialsForIndex: (idx: number) => string[];
  availableColors: string[];
  getAvailableColorsForIndex: (idx: number) => string[];
  resolvedModelName: string;
  resolvedMaterialName: string;
  resolvedDoorColor: string;
  resolvedBodyColor: string;
  resolvedGlassType: string;
  resolvedProfileColor: string;
  filteredCatalog: CatalogItem[];
  browsableCatalog: CatalogItem[];
  allVisualAttachmentItems: SpecItem[];
  pricingCalculations: ReturnType<typeof calculatePricing>;
  previewRows: Array<{ key: string; displayName: string; listPrice: number; campaignDiscount: number | ''; extraDiscount: number | ''; vatRate: number; incVatPrice: number }>;
  hasExtraDiscount: boolean;
  cabinetsCampaignSavings: number;
  otherRowsDiscountSavings: number;
  selectedStore: StoreLocation | undefined;
  catalogColorNames: string[];
  salesPoints: StoreLocation[];

  // Handlers
  handleExitAdminMode: () => void;
  saveCompatibilityRules: (rules: Record<string, Record<string, string[]>>) => Promise<void>;
  saveCatalog: (newCatalog: CatalogItem[]) => Promise<void>;
  upsertCatalogItems: (items: CatalogItem[]) => Promise<void>;
  deleteCatalogItemsByCode: (codes: string[]) => Promise<void>;
  handleSaveCatalogItem: (e: React.FormEvent) => void;
  handleEditCatalogItemClick: (item: CatalogItem) => void;
  handleDeleteCatalogItem: (code: string) => void;
  handleResetCatalogToDefault: () => void;
  handleResetMatrixToDefault: () => void;
  handleExportCatalog: () => void;
  handleExportCatalogExcel: () => void;
  handleImportCatalog: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleImportExcel: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCatalogItemImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleCatalogBrowserImageUpload: (code: string, file: File) => Promise<void>;
  handleMatrixColorImageUpload: (colorName: string, file: File) => Promise<void>;
  handleInstallClick: () => Promise<void>;
  handleDismissBanner: () => void;
  handleUpdatePriceRow: (key: string, fields: Partial<PricingRow>) => void;
  handleListPriceChange: (key: string, rawValue: string) => void;
  handleAddManualPriceRow: () => void;
  handleRemovePriceRow: (key: string) => void;
  handleAddSpecItemFromCatalog: (item: CatalogItem) => void;
  handleAddCustomSpecItem: () => void;
  handleUpdateSpecItem: (id: string, fields: Partial<SpecItem>) => void;
  handleRemoveSpecItem: (id: string) => void;
  handleTriggerUpload: (itemId: string) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleProjectImagesUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleProjectImagesDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  /** Returns false when the user cancels the confirmation — callers must not navigate in that case. */
  handleNewProposal: (type?: 'retail' | 'bulk') => boolean;
  handleExportDraft: () => void;
  handleTriggerImport: () => void;
  handleImportDraft: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTriggerXmlImport: () => void;
  handleImportXmlProject: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDragStart: (index: number) => void;
  handleDragOver: (e: React.DragEvent, index: number) => void;
  handleDragEnd: () => void;
  currentUser: User | null;
  userName: string;
  userRole: 'admin' | 'catalog_manager' | 'designer' | null;
  userAssignedSalesPoint: string;
  userPhone: string;
  loadingAuth: boolean;
  logout: () => Promise<void>;
  formatPhoneNumber: (value: string) => string;
  proposalId: string;
  elapsedSeconds: number;
  isProposalsModalOpen: boolean;
  setIsProposalsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readOnlyState: { isReadOnly: boolean; designerName?: string; originalDesignerId?: string };
  exitReadOnlyMode: () => void;
  saveProposalToCloud: (isAutoSave?: boolean) => Promise<void>;
  autoSaveStatus: 'saved' | 'saving' | 'unsaved' | 'error' | 'idle';
  lastSavedAt: Date | null;
  loadProposalFromCloud: (docData: any) => void;
  cloneProposalFromCloud: (docData: any) => void;
  logProposalActivity: (actionType: 'export_json' | 'print_pdf') => Promise<void>;
  pendingXmlImport: { xmlText: string; fileName: string; parsed: ParsedXmlProject } | null;
  handleConfirmXmlImport: (includeUnmatched: boolean) => void;
}

// ─── Context & Hook ──────────────────────────────────────────────────────────────

const AppContext = createContext<AppContextValue | null>(null);

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  // ── UI Mode ──
  const [mode, setMode] = useState<'welcome' | 'edit' | 'preview' | 'users' | 'analytics' | 'salesPoints'>('welcome');
  const [editTab, setEditTab] = useState<'client' | 'config' | 'pricing' | 'specs' | 'all'>('client');

  // ── Refs ──
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const xmlInputRef = useRef<HTMLInputElement>(null);
  const projectImageInputRef = useRef<HTMLInputElement>(null);
  const catalogImportRef = useRef<HTMLInputElement>(null);
  const excelImportRef = useRef<HTMLInputElement>(null);
  const fileMenuRef = useRef<HTMLDivElement>(null);

  // ── File Menu ──
  const [fileMenuOpen, setFileMenuOpen] = useState(false);
  const [uploadActiveItemId, setUploadActiveItemId] = useState<string | null>(null);

  // ── Auto Save State ──
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'unsaved' | 'error' | 'idle'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const isFirstRender = useRef(true);

  // ── XML Import Pending State ──
  const [pendingXmlImport, setPendingXmlImport] = useState<{
    xmlText: string;
    fileName: string;
    parsed: ParsedXmlProject;
  } | null>(null);

  // ── Sales Points State ──
  // Falls back to the built-in default list (with deterministic slug ids) until the
  // 'sales_points' collection has real docs — see initializeData() below. No automatic
  // writes happen; an admin must explicitly seed defaults from SalesPointManagement.
  const [salesPoints, setSalesPoints] = useState<StoreLocation[]>(
    () => STORE_LOCATIONS.map(s => ({ ...s, id: slugifyStoreName(s.name) }))
  );

  // ── Catalog State ──
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [isProposalsModalOpen, setIsProposalsModalOpen] = useState(false);
  const [readOnlyState, setReadOnlyState] = useState<{ isReadOnly: boolean; designerName?: string; originalDesignerId?: string }>({ isReadOnly: false });
  const [catalogSearch, setCatalogSearch] = useState('');
  const [selectedCatalogCategory, setSelectedCatalogCategory] = useState<string>('Aksesuar');
  const [isEditingCatalogItem, setIsEditingCatalogItem] = useState(false);
  const [editingItemCode, setEditingItemCode] = useState<string | null>(null);
  const [catalogForm, setCatalogForm] = useState<{ category: string; code: string; name: string; image: string | null; imageRemoved?: boolean }>({
    category: 'Aksesuar', code: '', name: '', image: null, imageRemoved: false,
  });
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [catalogActiveTab, setCatalogActiveTab] = useState<'products' | 'matrix'>('products');

  // ── Left Nav Rail (expand/collapse, persisted) ──
  const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem('intema_sidebar_expanded') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('intema_sidebar_expanded', String(isSidebarExpanded)); } catch { /* ignore */ }
  }, [isSidebarExpanded]);

  // ── Compatibility Rules ──
  const [compatibilityRules, setCompatibilityRules] = useState<Record<string, Record<string, string[]>>>({});
  const [selectedMatrixModel, setSelectedMatrixModel] = useState('');
  const [selectedMatrixMaterial, setSelectedMatrixMaterial] = useState('');
  const [newMatrixModelName, setNewMatrixModelName] = useState('');
  const [newMatrixMaterialName, setNewMatrixMaterialName] = useState('');
  const [newMatrixColorName, setNewMatrixColorName] = useState('');

  // ── PWA State ──
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [pwaInstalled, setPwaInstalled] = useState(false);

  // ── Core Proposal Data ──
  const [client, setClient] = useState<ClientMeta>(DEFAULT_CLIENT);
  const [config, setConfig] = useState<GlobalConfig>(DEFAULT_CONFIG);
  const [pricingRows, setPricingRows] = useState<PricingRow[]>(DEFAULT_PRICING_ROWS);
  const [salesPointDiscount, setSalesPointDiscount] = useState<number>(0);
  const [specItems, setSpecItems] = useState<SpecItem[]>([]);
  const [projectImages, setProjectImages] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // ── Bulk / Project Proposal State ──
  // Toplu Proje teklifinde her "daire tipi" kendi config/pricingRows/specItems/projectImages'ına
  // sahip bağımsız bir KitchenUnit'tir. `activeUnit`, o an düzenlenen daire tipini gösterir.
  const [proposalType, setProposalType] = useState<'retail' | 'bulk'>('retail');
  const [projectMeta, setProjectMeta] = useState<ProjectMeta>({ projectName: '', siteName: '' });
  const [units, setUnits] = useState<KitchenUnit[]>([]);
  const [activeUnitId, setActiveUnitId] = useState<string>('');
  const activeUnit = proposalType === 'bulk' ? units.find(u => u.id === activeUnitId) : undefined;

  const updateActiveUnit = (patch: Partial<KitchenUnit> | ((u: KitchenUnit) => Partial<KitchenUnit>)) => {
    setUnits(prev => prev.map(u => u.id === activeUnitId ? { ...u, ...(typeof patch === 'function' ? (patch as (u: KitchenUnit) => Partial<KitchenUnit>)(u) : patch) } : u));
  };

  const createDefaultUnit = (label = ''): KitchenUnit => ({
    id: 'unit_' + crypto.randomUUID().replace(/-/g, '').substring(0, 12),
    label,
    quantity: 1,
    config: DEFAULT_CONFIG,
    pricingRows: DEFAULT_PRICING_ROWS,
    specItems: [],
    projectImages: [],
  });

  const addUnit = () => {
    const newUnit = createDefaultUnit(`Daire Tipi ${units.length + 1}`);
    setUnits(prev => [...prev, newUnit]);
    setActiveUnitId(newUnit.id);
  };

  const removeUnit = (id: string) => {
    if (units.length <= 1) return; // en az bir daire tipi kalmalı
    const idx = units.findIndex(u => u.id === id);
    const remaining = units.filter(u => u.id !== id);
    setUnits(remaining);
    if (activeUnitId === id) {
      setActiveUnitId(remaining[Math.max(0, idx - 1)]?.id || remaining[0]?.id || '');
    }
  };

  const renameUnit = (id: string, label: string) => {
    setUnits(prev => prev.map(u => u.id === id ? { ...u, label } : u));
  };

  const setUnitQuantity = (id: string, quantity: number) => {
    setUnits(prev => prev.map(u => u.id === id ? { ...u, quantity: Math.max(1, quantity) } : u));
  };

  const setUnitManualPricing = (id: string, fields: Partial<Pick<KitchenUnit, 'manualUnitPrice' | 'manualVat'>>) => {
    setUnits(prev => prev.map(u => u.id === id ? { ...u, ...fields } : u));
  };

  // Effective "şu an düzenlenen" veri dilimi — toplu modda aktif daire tipi, perakende modda
  // tekil state. GlobalConfigPanel/PricingTable/SpecsSection/ProjectImagesCard bunlardan
  // okuyup yazdığı için her iki teklif türü için de değişmeden çalışır.
  const effectiveConfig = activeUnit ? activeUnit.config : config;
  const effectivePricingRows = activeUnit ? activeUnit.pricingRows : pricingRows;
  const effectiveSpecItems = activeUnit ? activeUnit.specItems : specItems;
  const effectiveProjectImages = activeUnit ? activeUnit.projectImages : projectImages;

  const effectiveSetConfig: React.Dispatch<React.SetStateAction<GlobalConfig>> = (value) => {
    if (activeUnit) {
      updateActiveUnit(u => ({ config: typeof value === 'function' ? (value as (p: GlobalConfig) => GlobalConfig)(u.config) : value }));
    } else {
      setConfig(value);
    }
  };
  const effectiveSetPricingRows: React.Dispatch<React.SetStateAction<PricingRow[]>> = (value) => {
    if (activeUnit) {
      updateActiveUnit(u => ({ pricingRows: typeof value === 'function' ? (value as (p: PricingRow[]) => PricingRow[])(u.pricingRows) : value }));
    } else {
      setPricingRows(value);
    }
  };
  const effectiveSetSpecItems: React.Dispatch<React.SetStateAction<SpecItem[]>> = (value) => {
    if (activeUnit) {
      updateActiveUnit(u => ({ specItems: typeof value === 'function' ? (value as (p: SpecItem[]) => SpecItem[])(u.specItems) : value }));
    } else {
      setSpecItems(value);
    }
  };
  const effectiveSetProjectImages: React.Dispatch<React.SetStateAction<string[]>> = (value) => {
    if (activeUnit) {
      updateActiveUnit(u => ({ projectImages: typeof value === 'function' ? (value as (p: string[]) => string[])(u.projectImages) : value }));
    } else {
      setProjectImages(value);
    }
  };

  // ── Spec Search ──
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  // ── Auth State ──
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [userRole, setUserRole] = useState<'admin' | 'catalog_manager' | 'designer' | null>(null);
  const [userAssignedSalesPoint, setUserAssignedSalesPoint] = useState<string>('');
  const [userPhone, setUserPhone] = useState<string>('');
  const [loadingAuth, setLoadingAuth] = useState(true);

  // ── Analytics Tracking State ──
  const [proposalId, setProposalId] = useState<string>(() => 'prop_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16));
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const hasLoggedSession = useRef(false);
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);

  // Only accrue "hazırlama süresi" while the tab is actually in the foreground —
  // a background/minimized tab must not inflate the average preparation time.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    };

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);



  const [activeSpecCategory, setActiveSpecCategory] = useState('Aksesuar');

  useEffect(() => {
    let unsubscribeRole: (() => void) | undefined;
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user && db) {
        if (!hasLoggedSession.current) {
          hasLoggedSession.current = true;
          setMode('welcome');
          const userDocRef = doc(db, 'users', user.uid);
          setDoc(userDocRef, {
            loginCount: increment(1),
            lastLoginAt: new Date().toISOString()
          }, { merge: true }).catch(err => console.error("Failed to update user login count:", err));
        }
        const userDocRef = doc(db, 'users', user.uid);
        unsubscribeRole = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.disabled === true) {
              signOut(auth);
              setUserRole(null);
              setCurrentUser(null);
              setUserName('');
              setUserAssignedSalesPoint('');
              setUserPhone('');
              setIsAdminMode(false);
              alert("Hesabınız yönetici tarafından devre dışı bırakılmıştır.");
            } else {
              const roleVal = (data.role as 'admin' | 'catalog_manager' | 'designer') || 'designer';
              setUserRole(roleVal);
              const fetchedName = data.name || user.displayName || '';
              setUserName(fetchedName);
              const fetchedSalesPoint = data.assignedSalesPoint || '';
              setUserAssignedSalesPoint(fetchedSalesPoint);
              const fetchedPhone = data.phone || '';
              setUserPhone(fetchedPhone);

              // Auto pre-fill client.designer if empty
              if (fetchedName) {
                setClient(prev => {
                  if (!prev.designer) {
                    return { ...prev, designer: fetchedName };
                  }
                  return prev;
                });
              }

              // Auto pre-fill client.storeName from the user's assigned sales point if empty
              if (fetchedSalesPoint) {
                setClient(prev => {
                  if (!prev.storeName) {
                    return { ...prev, storeName: fetchedSalesPoint };
                  }
                  return prev;
                });
              }

              // Auto pre-fill client.designerPhone from the user's own phone if empty.
              // Left empty when the user has no phone of their own — the cover then
              // falls back to the selected sales point's phone.
              if (fetchedPhone) {
                setClient(prev => {
                  if (!prev.designerPhone) {
                    return { ...prev, designerPhone: fetchedPhone };
                  }
                  return prev;
                });
              }

              if (roleVal === 'admin' || roleVal === 'catalog_manager') {
                setIsAdminMode(true);
              } else {
                setIsAdminMode(false);
              }
            }
          } else if (!docSnap.metadata.fromCache) {
            // Only treat this as a real deletion once the SERVER has confirmed the
            // document is gone. Right after clearing local storage (or on a fresh
            // device), Firestore can briefly report "not found" from an empty local
            // cache before the real server data arrives — acting on that transient
            // state would wrongly sign out a perfectly valid user.
            signOut(auth);
            setUserRole(null);
            setCurrentUser(null);
            setIsAdminMode(false);
            alert("Kullanıcı kaydınız bulunamadı veya silinmiştir.");
          }
          setLoadingAuth(false);
        }, (err) => {
          console.error("Error fetching user role:", err);
          setUserRole('designer');
          setLoadingAuth(false);
        });
      } else {
        if (unsubscribeRole) {
          unsubscribeRole();
          unsubscribeRole = undefined;
        }
        setUserRole(null);
        setIsAdminMode(false);
        setLoadingAuth(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeRole) unsubscribeRole();
    };
  }, []);

  const logout = async () => {
    await signOut(auth);
  };

  // ── Firebase Init ──────────────────────────────────────────────────────────────
  // Waits for Firebase Auth to resolve (currentUser) before opening any Firestore
  // listener. onAuthStateChanged always resolves asynchronously, even for an
  // already-persisted session — subscribing before it fires sends the very first
  // request unauthenticated, Firestore denies it, and the onSnapshot listener dies
  // permanently (it does not auto-retry once auth becomes valid), leaving the app
  // stuck on the localStorage/default fallback for the rest of the session.
  useEffect(() => {
    if (!currentUser) return;

    let unsubscribeCatalog: (() => void) | undefined;
    let unsubscribeRules: (() => void) | undefined;
    let unsubscribeSalesPoints: (() => void) | undefined;

    const initializeData = async () => {
      // 1. Load catalog
      if (db) {
        unsubscribeCatalog = onSnapshot(
          collection(db, 'intema_catalog'),
          async (snapshot) => {
            const items = snapshot.docs.map(d => d.data() as CatalogItem);
            if (items && items.length > 0) {
              const sanitized = items.map(item => ({
                ...item,
                code: item.code ? item.code.trim().toUpperCase() : '',
                name: item.name ? item.name.trim() : '',
                category: normalizeCategory(item.category),
                image: item.image || ''
              })).filter(i => i.code && i.name);

              // Sort catalog alphabetically by Category then by Code for consistent rendering
              sanitized.sort((a, b) => {
                const catCompare = a.category.localeCompare(b.category, 'tr');
                if (catCompare !== 0) return catCompare;
                return a.code.localeCompare(b.code, 'tr');
              });

              setCatalog(sanitized);
              const lightweightCatalog = sanitized.map(item => ({
                ...item,
                image: item.image && !item.image.startsWith('data:') ? item.image : ''
              }));
              safeLocalStorageSetItem('intema_catalog', JSON.stringify(lightweightCatalog));
            } else {
              console.warn('Katalog koleksiyonu boş görünüyor — otomatik veritabanı yazması yapılmıyor.');
              const saved = localStorage.getItem('intema_catalog');
              let fallbackCatalog: CatalogItem[] = [];
              if (saved) {
                try {
                  const parsed = JSON.parse(saved);
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    fallbackCatalog = parsed.map((item: any) => ({
                      ...item,
                      category: normalizeCategory(item.category)
                    }));
                  }
                } catch (e) {
                  fallbackCatalog = catalogData as CatalogItem[];
                }
              }
              if (!fallbackCatalog || fallbackCatalog.length === 0) {
                fallbackCatalog = catalogData as CatalogItem[];
              }

              setCatalog(fallbackCatalog);
            }
          },
          (error) => {
            console.error('Firebase catalog load failed, using localStorage:', error);
            const saved = localStorage.getItem('intema_catalog');
            if (saved) {
              try {
                const parsed = JSON.parse(saved);
                setCatalog(parsed.map((item: any) => ({ ...item, category: normalizeCategory(item.category) })));
              } catch (e) {
                setCatalog(catalogData as CatalogItem[]);
              }
            } else {
              setCatalog(catalogData as CatalogItem[]);
            }
          }
        );
      } else {
        const saved = localStorage.getItem('intema_catalog');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setCatalog(parsed.map((item: any) => ({ ...item, category: normalizeCategory(item.category) })));
          } catch (e) {
            setCatalog(catalogData as CatalogItem[]);
          }
        } else {
          setCatalog(catalogData as CatalogItem[]);
        }
      }

      // 2. Load compatibility rules
      if (db) {
        unsubscribeRules = onSnapshot(
          doc(db, 'intema_config', 'compatibility_rules'),
          async (rulesDoc) => {
            if (rulesDoc.exists() && rulesDoc.data()?.rules) {
              setCompatibilityRules(rulesDoc.data().rules);
            } else {
              if (navigator.onLine) {
                await setDoc(doc(db, 'intema_config', 'compatibility_rules'), { rules: KITCHEN_COMPATIBILITY_RULES_DEFAULT });
              }
              setCompatibilityRules(KITCHEN_COMPATIBILITY_RULES_DEFAULT);
            }
          },
          (error) => {
            console.error('Firebase rules load failed, using localStorage:', error);
            const saved = localStorage.getItem('intema_compatibility_rules');
            setCompatibilityRules(saved ? JSON.parse(saved) : KITCHEN_COMPATIBILITY_RULES_DEFAULT);
          }
        );
      } else {
        const saved = localStorage.getItem('intema_compatibility_rules');
        setCompatibilityRules(saved ? JSON.parse(saved) : KITCHEN_COMPATIBILITY_RULES_DEFAULT);
      }

      // 3. Load sales points — falls back to the built-in default list (state initializer)
      // while the collection is empty; no automatic writes (mirrors the catalog's approach above).
      if (db) {
        unsubscribeSalesPoints = onSnapshot(
          collection(db, 'sales_points'),
          (snapshot) => {
            if (!snapshot.empty) {
              const points = snapshot.docs
                .map(d => ({ id: d.id, ...(d.data() as Omit<StoreLocation, 'id'>) }))
                .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
              setSalesPoints(points);
            }
          },
          (error) => {
            console.error('Firebase sales points load failed, using built-in default list:', error);
          }
        );
      }
    };
    initializeData();

    return () => {
      if (unsubscribeCatalog) unsubscribeCatalog();
      if (unsubscribeRules) unsubscribeRules();
      if (unsubscribeSalesPoints) unsubscribeSalesPoints();
    };
  }, [currentUser]);



  // ── File Menu Outside Click ──
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (fileMenuRef.current && !fileMenuRef.current.contains(e.target as Node)) {
        setFileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // ── PWA Init ──
  const isIOS = useMemo(() => /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream, []);
  const isSafari = useMemo(() => /^((?!chrome|android).)*safari/i.test(navigator.userAgent), []);

  useEffect(() => {
    const checkStandalone = () => {
      setIsStandalone(
        window.matchMedia('(display-mode: standalone)').matches ||
        navigator.standalone === true ||
        document.referrer.includes('android-app://')
      );
    };
    checkStandalone();

    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e: MediaQueryListEvent) => setIsStandalone(e.matches);
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    const handleBeforeInstallPrompt = (e: Event) => { e.preventDefault(); setDeferredPrompt(e as BeforeInstallPromptEvent); };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const handleAppInstalled = () => {
      setIsStandalone(true); setDeferredPrompt(null); setPwaInstalled(true);
      localStorage.setItem('pwa_installed', 'true');
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    setIsBannerDismissed(localStorage.getItem('pwa_install_banner_dismissed_date') === new Date().toDateString());
    const installed = localStorage.getItem('pwa_installed') === 'true';
    setPwaInstalled(installed);

    if ('getInstalledRelatedApps' in navigator && navigator.getInstalledRelatedApps) {
      navigator.getInstalledRelatedApps().then((apps) => {
        if (apps.length > 0) { setPwaInstalled(true); localStorage.setItem('pwa_installed', 'true'); }
      }).catch(console.error);
    }

    return () => {
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // ── LocalStorage Draft Auto-save & Restore ──
  const migratePricingRows = (rows: PricingRow[]): PricingRow[] => {
    const hasOldKey = rows.some(r => r.key === 'appliances_sinks_taps');
    if (!hasOldKey) return rows;
    const oldRow = rows.find(r => r.key === 'appliances_sinks_taps')!;
    const filtered = rows.filter(r => r.key !== 'appliances_sinks_taps');
    return [
      ...filtered,
      { key: 'appliances', categoryName: 'Cihaz',   listPrice: oldRow.listPrice, campaignDiscount: oldRow.campaignDiscount, extraDiscount: oldRow.extraDiscount, vatRate: 0.20 },
      { key: 'sinks',      categoryName: 'Eviye',   listPrice: '', campaignDiscount: '', extraDiscount: '', vatRate: 0.20 },
      { key: 'taps',       categoryName: 'Armatür', listPrice: '', campaignDiscount: '', extraDiscount: '', vatRate: 0.20 },
    ];
  };

  const healSpecItems = (items: SpecItem[]): SpecItem[] => {
    return items.map(item => {
      if (item.image && item.image.startsWith('data:')) return item;
      const match = catalog.find(c =>
        c.code.trim().toUpperCase() === item.code.trim().toUpperCase() &&
        c.category.trim().toLowerCase() === item.category.trim().toLowerCase()
      );
      if (match && match.image && match.image !== item.image) return { ...item, image: match.image };
      return item;
    });
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem('intema_quoting_draft');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.client && parsed?.config && parsed?.pricingRows && parsed?.specItems) {
          setClient(parsed.client);
          setConfig(parsed.config);
          setPricingRows(migratePricingRows(parsed.pricingRows));
          setSalesPointDiscount(parsed.salesPointDiscount || 0);
          setSpecItems(healSpecItems(parsed.specItems));
          setProjectImages(parsed.projectImages || []);
        }
      }
    } catch (e) { console.error('Draft restore failed:', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('intema_quoting_draft', JSON.stringify({ client, config, pricingRows, salesPointDiscount, specItems, projectImages }));
    } catch (e) { console.warn('Draft auto-save failed:', e); }
  }, [client, config, pricingRows, salesPointDiscount, specItems, projectImages]);

  // ── Compatibility Sync ──
  // effectiveConfig/effectiveSetConfig/effectiveSetSpecItems so these auto-sync
  // behaviors apply to the active bulk unit too, not just the retail singular state.
  useEffect(() => {
    if (!effectiveConfig.model || effectiveConfig.model === 'CUSTOM') return;
    const modelRules = compatibilityRules[effectiveConfig.model];
    if (!modelRules) return;
    if (effectiveConfig.material && effectiveConfig.material !== 'CUSTOM') {
      if (!Object.keys(modelRules).includes(effectiveConfig.material)) {
        effectiveSetConfig(prev => ({ ...prev, material: '', customMaterial: '', doorColor: '', customDoorColor: '' }));
        return;
      }
    }
    if (effectiveConfig.material && effectiveConfig.material !== 'CUSTOM' && effectiveConfig.doorColor && effectiveConfig.doorColor !== 'CUSTOM') {
      if (!(modelRules[effectiveConfig.material] || []).includes(effectiveConfig.doorColor)) {
        effectiveSetConfig(prev => ({ ...prev, doorColor: '', customDoorColor: '' }));
      }
    }
  }, [effectiveConfig.model, effectiveConfig.material, effectiveConfig.doorColor, compatibilityRules, activeUnitId]);

  // Keep Kapak Rengi (Renk) specItems synced with Mutfak Konfigürasyonu doorColor selections
  useEffect(() => {
    const doorColors: string[] = [];

    // Main combination door color
    if (effectiveConfig.doorColor && effectiveConfig.doorColor !== 'CUSTOM') {
      doorColors.push(effectiveConfig.doorColor);
    } else if (effectiveConfig.doorColor === 'CUSTOM' && effectiveConfig.customDoorColor) {
      doorColors.push(effectiveConfig.customDoorColor);
    }

    // Extra combinations door colors
    if (Array.isArray(effectiveConfig.extraDoorColors)) {
      effectiveConfig.extraDoorColors.forEach(c => {
        if (c && c !== 'CUSTOM' && !doorColors.includes(c)) {
          doorColors.push(c);
        }
      });
    }

    effectiveSetSpecItems(prev => {
      if (doorColors.length === 0) {
        const hasDoorItems = prev.some(item => item.category === 'Renk' || item.category === 'Kapak Rengi');
        if (!hasDoorItems) return prev;
        return prev.filter(item => item.category !== 'Renk' && item.category !== 'Kapak Rengi');
      }

      let updated = [...prev];

      doorColors.forEach((colorName, idx) => {
        const colorCatalogItem = findCatalogColorItem(colorName, catalog);
        const code = colorCatalogItem?.code || colorName;
        const name = colorCatalogItem?.name && colorCatalogItem.name !== 'Lütfen yerleşim bilgisi giriniz'
          ? colorCatalogItem.name
          : `${colorName} Kapak Rengi`;
        const image = colorCatalogItem?.image || null;

        const existingDoorItems = updated.filter(item => item.category === 'Renk' || item.category === 'Kapak Rengi');
        
        if (existingDoorItems[idx]) {
          const itemToUpdateIndex = updated.findIndex(item => item.id === existingDoorItems[idx].id);
          if (itemToUpdateIndex >= 0) {
            const currentItem = updated[itemToUpdateIndex];
            if (currentItem.code !== code || currentItem.name !== name || (image && currentItem.image !== image)) {
              updated[itemToUpdateIndex] = {
                ...currentItem,
                code,
                name,
                image: image || currentItem.image || null,
              };
            }
          }
        } else {
          updated.splice(idx, 0, {
            id: `door_color_${Date.now()}_${idx}`,
            category: 'Renk',
            code,
            name,
            image,
          });
        }
      });

      return updated;
    });
  }, [effectiveConfig.doorColor, effectiveConfig.customDoorColor, JSON.stringify(effectiveConfig.extraDoorColors), catalog, activeUnitId]);

  // Keep the Gövde Rengi specItems synced with the body colors picked in Mutfak
  // Konfigürasyonu — one row per color, image filled in automatically.
  useEffect(() => {
    const bodyColors = collectBodyColors(effectiveConfig);
    effectiveSetSpecItems(prev => syncBodyColorSpecItems(prev, bodyColors, catalog));
  }, [effectiveConfig.bodyColor, JSON.stringify(effectiveConfig.extraBodyColors), catalog, activeUnitId]);

  // ── Derived Values ─────────────────────────────────────────────────────────────

  const MODELS = useMemo(() => [
    ...Object.keys(compatibilityRules).filter(m => m !== 'CUSTOM'), 'CUSTOM'
  ], [compatibilityRules]);

  const catalogColorNames = useMemo(() => {
    const colors = catalog.filter(i => i.category === 'Renk').map(i => i.code);
    return Array.from(new Set(colors)).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [catalog]);

  const getAvailableMaterialsForIndex = useMemo(() => {
    return (idx: number): string[] => {
      const all = Array.from(new Set([
        ...Object.values(compatibilityRules).flatMap(r => Object.keys(r)), 'CUSTOM'
      ])).sort((a, b) => a === 'CUSTOM' ? 1 : b === 'CUSTOM' ? -1 : a.localeCompare(b, 'tr'));

      const modelForSlot = idx === 0 ? effectiveConfig.model : (effectiveConfig.extraModels?.[idx - 1] || '');
      if (!modelForSlot || modelForSlot === 'CUSTOM') return all;

      const rules = compatibilityRules[modelForSlot];
      if (!rules) return all;

      const result = Array.from(new Set([...Object.keys(rules), 'CUSTOM'])).sort((a, b) => a === 'CUSTOM' ? 1 : b === 'CUSTOM' ? -1 : a.localeCompare(b, 'tr'));
      return result.length > 1 ? result : all;
    };
  }, [effectiveConfig.model, effectiveConfig.extraModels, compatibilityRules]);

  const availableMaterials = useMemo(() => getAvailableMaterialsForIndex(0), [getAvailableMaterialsForIndex]);

  const getAvailableColorsForIndex = useMemo(() => {
    return (idx: number): string[] => {
      const modelForSlot = idx === 0 ? effectiveConfig.model : (effectiveConfig.extraModels?.[idx - 1] || '');
      const matForSlot = idx === 0 ? effectiveConfig.material : (effectiveConfig.extraMaterials?.[idx - 1] || '');

      const selectedModels = modelForSlot && modelForSlot !== 'CUSTOM'
        ? [modelForSlot]
        : [effectiveConfig.model, ...(effectiveConfig.extraModels || [])].filter(m => m && m !== 'CUSTOM');

      const selectedMaterials = matForSlot && matForSlot !== 'CUSTOM'
        ? [matForSlot]
        : [effectiveConfig.material, ...(effectiveConfig.extraMaterials || [])].filter(m => m && m !== 'CUSTOM');

      if (selectedModels.length === 0 || selectedMaterials.length === 0) return catalogColorNames;

      const allowedColorsSet = new Set<string>();
      selectedModels.forEach(m => {
        const rules = compatibilityRules[m];
        if (rules) {
          selectedMaterials.forEach(mat => {
            const colors = rules[mat];
            if (colors) {
              colors.forEach(c => allowedColorsSet.add(c));
            }
          });
        }
      });

      const result = Array.from(allowedColorsSet).sort((a, b) => a.localeCompare(b, 'tr'));
      return result.length > 0 ? result : catalogColorNames;
    };
  }, [effectiveConfig.model, effectiveConfig.extraModels, effectiveConfig.material, effectiveConfig.extraMaterials, catalogColorNames, compatibilityRules]);

  const availableColors = useMemo(() => getAvailableColorsForIndex(0), [getAvailableColorsForIndex]);

  const { resolvedModelName, resolvedMaterialName, resolvedDoorColor, resolvedBodyColor, resolvedGlassType, resolvedProfileColor } = useMemo(
    () => resolveConfigNames(effectiveConfig),
    [effectiveConfig.model, effectiveConfig.extraModels, effectiveConfig.customModel,
     effectiveConfig.material, effectiveConfig.extraMaterials, effectiveConfig.customMaterial,
     effectiveConfig.doorColor, effectiveConfig.extraDoorColors, effectiveConfig.customDoorColor,
     effectiveConfig.bodyColor, effectiveConfig.extraBodyColors,
     effectiveConfig.glassType, effectiveConfig.extraGlassTypes,
     effectiveConfig.profileColor, effectiveConfig.extraProfileColors]
  );

  const filteredCatalog = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = trLower(searchQuery);
    return catalog.filter(item =>
      trLower(item.code).includes(q) ||
      trLower(item.name).includes(q) ||
      trLower(item.category).includes(q)
    ).slice(0, 15);
  }, [searchQuery, catalog]);

  const browsableCatalog = useMemo(() => {
    if (searchQuery.trim()) {
      const q = trLower(searchQuery);
      return catalog.filter(item =>
        trLower(item.code).includes(q) ||
        trLower(item.name).includes(q) ||
        trLower(item.category).includes(q)
      ).slice(0, 200);
    }
    return catalog.filter(item => item.category === activeSpecCategory).slice(0, 200);
  }, [activeSpecCategory, searchQuery, catalog]);

  const allVisualAttachmentItems = useMemo(() => sortVisualAttachmentItems(effectiveSpecItems), [effectiveSpecItems]);

  const pricingCalculations = useMemo(() => calculatePricing(effectivePricingRows, resolvedModelName), [effectivePricingRows, resolvedModelName]);

  const previewPricing = useMemo(() => buildPreviewPricing(pricingCalculations), [pricingCalculations]);

  const safeLocalStorageSetItem = (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch (err) {
      console.warn(`LocalStorage write for "${key}" skipped due to quota limits. Data is safely saved in Firestore.`, err);
    }
  };

function safeDocId(code: string): string {
  if (!code) return '';
  return code
    .trim()
    .toUpperCase()
    .replace(/\//g, '_SLASH_')
    .replace(/\./g, '_DOT_')
    .replace(/#/g, '_HASH_')
    .replace(/\$/g, '_DOLLAR_')
    .replace(/\[/g, '_LBRACK_')
    .replace(/\]/g, '_RBRACK_');
}

  const catalogSaveQueue = useRef<Promise<void>>(Promise.resolve());

  const saveCatalog = (newCatalog: CatalogItem[]): Promise<void> => {
    const run = catalogSaveQueue.current.then(() => saveCatalogInternal(newCatalog));
    catalogSaveQueue.current = run.catch(() => {});
    return run;
  };

  // Lightweight upsert for one or more KNOWN items — writes only those documents,
  // no full-collection read. Use for single add/edit/image-update and merge-only
  // imports (Excel/JSON), which never need to delete anything they don't already
  // know about. Only use saveCatalog (full read+diff+delete) for genuine
  // replace-everything operations like "reset to factory defaults".
  const upsertCatalogItems = (items: CatalogItem[]): Promise<void> => {
    const sanitized = items.map(item => ({
      ...item,
      code: item.code ? item.code.trim().toUpperCase() : '',
      name: item.name ? item.name.trim() : '',
      category: normalizeCategory(item.category, selectedCatalogCategory),
      image: item.image || ''
    })).filter(i => i.code !== '' && i.name !== '');

    if (sanitized.length === 0) return Promise.resolve();

    const run = catalogSaveQueue.current.then(async () => {
      const estimateBytes = (obj: any) => JSON.stringify(obj).length;
      const MAX_DOC_BYTES = 700 * 1024; // 700KB safety limit per document (Firestore limit is 1MB)

      if (db) {
        let batch = writeBatch(db);
        let count = 0;
        for (const item of sanitized) {
          const docId = safeDocId(item.code);
          let payload: CatalogItem = { ...item };
          if (estimateBytes(payload) > MAX_DOC_BYTES) {
            console.warn(`Product code ${item.code} document size exceeds limit. Dropping oversized image.`);
            payload = { ...item, image: '' };
          }
          batch.set(doc(db, 'intema_catalog', docId), payload, { merge: true }); count++;
          if (count >= 25) { await batch.commit(); batch = writeBatch(db); count = 0; }
        }
        if (count > 0) await batch.commit();
      }

      // Firestore confirmed (or no backend configured) — now merge into local state.
      setCatalog(prev => {
        const map = new Map(prev.map(i => [safeDocId(i.code), i] as const));
        sanitized.forEach(item => map.set(safeDocId(item.code), item));
        const merged = Array.from(map.values());
        const lightweight = merged.map(i => ({ ...i, image: i.image && !i.image.startsWith('data:') ? i.image : '' }));
        safeLocalStorageSetItem('intema_catalog', JSON.stringify(lightweight));
        return merged;
      });
    });

    catalogSaveQueue.current = run.catch((err) => {
      console.error('Firebase catalog upsert failed:', err);
      alert('❌ Ürün buluta kaydedilirken bir hata oluştu.\n\nHata detayı: ' + (err as Error).message);
    });
    return run;
  };

  // Lightweight delete for KNOWN codes — deletes only those documents, no
  // full-collection read. Use for single-item delete and bulk-delete-of-selected.
  const deleteCatalogItemsByCode = (codes: string[]): Promise<void> => {
    const docIds = Array.from(new Set(codes.map(c => safeDocId(c)).filter(Boolean)));
    if (docIds.length === 0) return Promise.resolve();

    const run = catalogSaveQueue.current.then(async () => {
      if (db) {
        let batch = writeBatch(db);
        let count = 0;
        for (const docId of docIds) {
          batch.delete(doc(db, 'intema_catalog', docId)); count++;
          if (count >= 25) { await batch.commit(); batch = writeBatch(db); count = 0; }
        }
        if (count > 0) await batch.commit();
      }

      setCatalog(prev => {
        const remaining = prev.filter(item => !docIds.includes(safeDocId(item.code)));
        const lightweight = remaining.map(i => ({ ...i, image: i.image && !i.image.startsWith('data:') ? i.image : '' }));
        safeLocalStorageSetItem('intema_catalog', JSON.stringify(lightweight));
        return remaining;
      });
    });

    catalogSaveQueue.current = run.catch((err) => {
      console.error('Firebase catalog delete failed:', err);
      alert('❌ Ürün(ler) buluttan silinirken bir hata oluştu.\n\nHata detayı: ' + (err as Error).message);
    });
    return run;
  };

  const saveCatalogInternal = async (newCatalog: CatalogItem[]) => {
    const sanitized = newCatalog.map(item => ({
      ...item,
      code: item.code ? item.code.trim().toUpperCase() : '',
      name: item.name ? item.name.trim() : '',
      category: normalizeCategory(item.category, selectedCatalogCategory),
      image: item.image || ''
    })).filter(i => i.code !== '' && i.name !== '');

    // Deduplicate by safeDocId to prevent "Multiple writes to document" Firestore batch crash
    const dedupedMap = new Map<string, CatalogItem>();
    sanitized.forEach(item => {
      const docId = safeDocId(item.code);
      if (docId) {
        const existing = dedupedMap.get(docId);
        dedupedMap.set(docId, {
          ...item,
          image: item.image || existing?.image || ''
        });
      }
    });
    const sanitizedCatalog = Array.from(dedupedMap.values());

    if (sanitizedCatalog.length === 0 && catalog.length > 0) {
      console.warn('saveCatalog received empty catalog, aborting to prevent data loss.');
      return;
    }

    if (db) {
      try {
        const querySnapshot = await getDocs(collection(db, 'intema_catalog'));
        const existingDocsMap = new Map<string, any>();
        querySnapshot.docs.forEach(d => {
          existingDocsMap.set(d.id, d.data());
        });

        // Match both legacy __SLASH__ and new _SLASH_ document IDs to prevent accidental deletes
        const newCodesSet = new Set(
          sanitizedCatalog.flatMap(c => [
            safeDocId(c.code),
            c.code.trim().toUpperCase().replace(/\//g, '__SLASH__').replace(/\./g, '__DOT__'),
            c.code.trim().toUpperCase().replace(/\//g, '_SLASH_').replace(/\./g, '_DOT_')
          ])
        );
        const toDelete = Array.from(existingDocsMap.keys()).filter(code => !newCodesSet.has(code));
        
        let batch = writeBatch(db);
        let count = 0;

        // 1. WRITE NEW & UPDATED ITEMS FIRST
        const estimateBytes = (obj: any) => JSON.stringify(obj).length;
        const MAX_DOC_BYTES = 700 * 1024; // 700KB safety limit per document (Firestore limit is 1MB)

        for (const item of sanitizedCatalog) {
          const docId = safeDocId(item.code);
          const existingData = existingDocsMap.get(docId);
          let finalImage = item.image || existingData?.image || '';
          let payload: CatalogItem = {
            ...item,
            image: finalImage,
          };

          if (estimateBytes(payload) > MAX_DOC_BYTES) {
            console.warn(`Product code ${item.code} document size exceeds limit. Dropping oversized image.`);
            finalImage = '';
            payload = { ...item, image: '' };
          }

          batch.set(doc(db, 'intema_catalog', docId), payload, { merge: true }); count++;
          if (count >= 25) { await batch.commit(); batch = writeBatch(db); count = 0; }
        }

        // 2. DELETE OBSOLETE ITEMS SECOND
        for (const code of toDelete) {
          batch.delete(doc(db, 'intema_catalog', code)); count++;
          if (count >= 25) { await batch.commit(); batch = writeBatch(db); count = 0; }
        }

        if (count > 0) await batch.commit();

        // ONLY UPDATE LOCAL STATE & LOCALSTORAGE AFTER FIRESTORE CONFIRMS WRITE SUCCESS
        setCatalog(sanitizedCatalog);
        // Strip heavy base64 images from localStorage so backup stays lightweight (~100KB) and avoids 5MB quota errors
        const lightweightCatalog = sanitizedCatalog.map(item => ({
          ...item,
          image: item.image && !item.image.startsWith('data:') ? item.image : ''
        }));
        safeLocalStorageSetItem('intema_catalog', JSON.stringify(lightweightCatalog));
      } catch (err) {
        console.error('Firebase catalog sync failed:', err);
        alert('❌ Ürün kataloğu buluta kaydedilirken bir hata oluştu ve değişiklikler geri alındı.\n\nHata detayı: ' + (err as Error).message);
        throw err;
      }
    } else {
      setCatalog(sanitizedCatalog);
      const lightweightCatalog = sanitizedCatalog.map(item => ({
        ...item,
        image: item.image && !item.image.startsWith('data:') ? item.image : ''
      }));
      safeLocalStorageSetItem('intema_catalog', JSON.stringify(lightweightCatalog));
    }
  };

  const selectedStore = useMemo(() => salesPoints.find(s => s.name === client.storeName), [client.storeName, salesPoints]);

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const handleExitAdminMode = () => {
    if (window.confirm('Yönetici modunu kapatmak ve katalog yönetim kartını gizlemek istediğinize emin misiniz? Tekrar açmak için sayfayı yenilemeniz yeterli olacaktır.')) {
      setIsAdminMode(false);
      setIsCatalogModalOpen(false);
    }
  };

  const saveCompatibilityRules = async (newRules: Record<string, Record<string, string[]>>) => {
    setCompatibilityRules(newRules);
    localStorage.setItem('intema_compatibility_rules', JSON.stringify(newRules));
    if (db) {
      try { await setDoc(doc(db, 'intema_config', 'compatibility_rules'), { rules: newRules }); }
      catch (err) { console.error('Firebase rules sync failed:', err); }
    }
  };

  const handleSaveCatalogItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!catalogForm.code.trim() || !catalogForm.name.trim()) {
      alert('Lütfen ürün kodu ve adını doldurunuz.'); return;
    }
    const formattedCode = catalogForm.code.trim();
    const formattedName = catalogForm.name.trim();
    const isDuplicate = catalog.some(item =>
      item.code.trim().toUpperCase() === formattedCode.toUpperCase() &&
      (!isEditingCatalogItem || item.code.trim().toUpperCase() !== editingItemCode?.trim().toUpperCase())
    );
    if (isDuplicate) { alert(`Bu ürün kodu (${formattedCode}) zaten katalogda kayıtlıdır.`); return; }
    if (isEditingCatalogItem && editingItemCode) {
      const editUpper = editingItemCode.trim().toUpperCase();
      const newUpper = formattedCode.toUpperCase();
      const existingItem = catalog.find(item => item.code.trim().toUpperCase() === editUpper);
      
      const finalImage = catalogForm.imageRemoved
        ? ''
        : ((catalogForm.image !== null && catalogForm.image !== '') ? catalogForm.image : (existingItem?.image || null));

      const updatedItem: CatalogItem = { category: catalogForm.category, code: formattedCode, name: formattedName, image: finalImage || '' };
      await upsertCatalogItems([updatedItem]);
      if (editUpper !== newUpper) {
        // Code changed: the old document ID is now stale, remove it so it doesn't linger as a duplicate.
        await deleteCatalogItemsByCode([editingItemCode]);
      }

      setSpecItems(prev => prev.map(spec => {
        const specUpper = spec.code.trim().toUpperCase();
        if (specUpper === editUpper || specUpper === newUpper) {
          return {
            ...spec,
            category: catalogForm.category,
            code: formattedCode,
            name: formattedName,
            image: finalImage || null
          };
        }
        return spec;
      }));

      alert('Ürün başarıyla güncellendi.');
    } else {
      await upsertCatalogItems([{ category: catalogForm.category, code: formattedCode, name: formattedName, image: catalogForm.image || '' }]);
      alert('Yeni ürün kataloğa başarıyla eklendi.');
    }
    setCatalogForm({ category: selectedCatalogCategory, code: '', name: '', image: null, imageRemoved: false });
    setIsEditingCatalogItem(false);
    setEditingItemCode(null);
  };

  const handleEditCatalogItemClick = (item: CatalogItem) => {
    setIsEditingCatalogItem(true);
    setEditingItemCode(item.code);
    setCatalogForm({ category: item.category, code: item.code, name: item.name, image: item.image || null, imageRemoved: false });
  };

  const handleDeleteCatalogItem = async (code: string) => {
    if (window.confirm('Bu ürünü katalogdan silmek istediğinize emin misiniz?')) {
      await deleteCatalogItemsByCode([code]);
      if (editingItemCode?.trim().toUpperCase() === code.trim().toUpperCase()) {
        setCatalogForm({ category: selectedCatalogCategory, code: '', name: '', image: null, imageRemoved: false });
        setIsEditingCatalogItem(false); setEditingItemCode(null);
      }
    }
  };

  const handleResetCatalogToDefault = async () => {
    if (window.confirm('Kataloğu orijinal fabrika ayarlarına döndürmek istediğinize emin misiniz? Eklediğiniz tüm ürünler ve yaptığınız değişiklikler silinecektir. Matris kuralları korunacaktır.')) {
      await saveCatalog(catalogData as CatalogItem[]);
      alert('Ürün kataloğu başarıyla orijinal ayarlarına sıfırlandı.');
    }
  };

  const handleResetMatrixToDefault = () => {
    if (window.confirm('Model-Renk matrisini orijinal fabrika ayarlarına döndürmek istediğinize emin misiniz? Matristeki tüm değişiklikleriniz silinecektir. Ürün kataloğu korunacaktır.')) {
      saveCompatibilityRules(KITCHEN_COMPATIBILITY_RULES_DEFAULT);
      alert('Model-Renk matrisi başarıyla orijinal ayarlarına sıfırlandı.');
    }
  };

  const handleExportCatalog = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(catalog, null, 2));
    const a = document.createElement('a');
    a.setAttribute('href', dataStr);
    a.setAttribute('download', `intema_katalog_${new Date().toISOString().slice(0, 10)}.json`);
    document.body.appendChild(a); a.click(); a.remove();
  };

  const handleExportCatalogExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(catalog.map(item => {
        const isBase64 = item.image && item.image.startsWith('data:');
        return {
          'Kategori': item.category === 'Renk' ? 'Kapak Rengi' : item.category,
          'Ürün Kodu': item.code,
          'Ürün Adı': item.name,
          'Görsel URL': isBase64 ? '[Base64 Görsel]' : (item.image || '')
        };
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Katalog');
      XLSX.writeFile(workbook, `intema_katalog_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err: any) {
      console.error(err);
      alert('Excel dışa aktarılırken hata oluştu: ' + err.message);
    }
  };

  const handleImportCatalog = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        if (Array.isArray(parsed) && parsed.every(item => item?.category && item?.code && item?.name)) {
          // JSON içe aktarma sadece EKLER/GÜNCELLER, hiçbir şeyi silmez — bu yüzden
          // tüm koleksiyonu değil, sadece dosyadaki ürünleri yazmak yeterli.
          const itemsToUpsert: CatalogItem[] = parsed.map((item: any) => {
            const key = item.code.trim().toUpperCase();
            const existing = catalog.find(c => c.code.trim().toUpperCase() === key);
            return { ...item, image: item.image || existing?.image || '' };
          });
          await upsertCatalogItems(itemsToUpsert);

          const firstCategory = parsed[0]?.category;
          if (firstCategory) {
            setSelectedCatalogCategory(firstCategory);
            setActiveSpecCategory(firstCategory);
          }

          alert(`${parsed.length} adet ürün başarıyla içe aktarıldı ve güncellendi!`);
        } else {
          alert('Geçersiz katalog dosyası formatı.');
        }
      } catch (err) { alert('Katalog dosyası okunurken hata oluştu: ' + (err as Error).message); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleDownloadExcelTemplate = () => {
    const sampleData = [
      { 'Kategori': 'Aksesuar', 'Kod': 'AKS-101', 'İsim': 'Ahşap Baharatlık Modülü', 'Görsel URL': '' },
      { 'Kategori': 'Cihaz', 'Kod': 'FRN-202', 'İsim': 'Ankastre 60cm Fırın Siyah', 'Görsel URL': '' },
      { 'Kategori': 'Kulp', 'Kod': 'KLP-303', 'İsim': 'Mat Siyah Profil Kulp', 'Görsel URL': '' },
      { 'Kategori': 'Tezgâh', 'Kod': 'TZG-404', 'İsim': 'Dekton Mermer Dokulu Tezgâh', 'Görsel URL': '' },
    ];
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Katalog Şablonu');
    XLSX.writeFile(workbook, 'intema_katalog_ornek_sablon.xlsx');
  };

  const handleImportExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingExcel(true);
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async (event) => {
      try {
        const buffer = event.target?.result as ArrayBuffer;
        if (!buffer) throw new Error('Dosya okunamadı.');

        // Extract embedded cell images (Excel 365 & Classic Drawings)
        const { byRow, mediaList } = await extractExcelImages(buffer);

        const workbook = XLSX.read(buffer, { type: 'array' });
        let rows: any[][] = [];

        for (const sheetName of workbook.SheetNames) {
          const sheet = workbook.Sheets[sheetName];
          const candidateRows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
          if (candidateRows && candidateRows.some(r => Array.isArray(r) && r.some(cell => String(cell || '').trim() !== ''))) {
            rows = candidateRows;
            break;
          }
        }

        if (!rows || rows.length === 0) {
          alert('Excel dosyası boş veya okunamadı.');
          setIsUploadingExcel(false);
          return;
        }

        const stripAccents = (str: string) => (str || '')
          .toLowerCase()
          .replace(/i̇/g, 'i').replace(/ı/g, 'i')
          .replace(/ş/g, 's').replace(/ç/g, 'c')
          .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ö/g, 'o')
          .replace(/[^a-z0-9]/g, '');

        // Smart Header Row Scoring (scans first 15 rows to find the true table header)
        let bestHeaderRowIdx = -1;
        let maxScore = -1;

        const maxScanRows = Math.min(rows.length, 15);
        for (let r = 0; r < maxScanRows; r++) {
          const row = rows[r];
          if (!Array.isArray(row)) continue;
          let score = 0;
          row.forEach(cell => {
            const norm = stripAccents(String(cell || ''));
            if (['kod', 'code', 'urunkodu', 'productcode', 'malzemekodu', 'stokkodu', 'artno', 'sku'].includes(norm)) score += 3;
            if (['isim', 'name', 'urunadi', 'productname', 'aciklama', 'description', 'tanim', 'model'].includes(norm)) score += 3;
            if (['kategori', 'category', 'grup', 'tur', 'katalog', 'urungrubu'].includes(norm)) score += 2;
            if (['gorsel', 'image', 'resim', 'fotograf', 'picture'].includes(norm)) score += 2;
          });
          if (score > maxScore) {
            maxScore = score;
            bestHeaderRowIdx = r;
          }
        }

        if (bestHeaderRowIdx === -1 || maxScore === 0) {
          bestHeaderRowIdx = rows.findIndex(r => Array.isArray(r) && r.filter(c => String(c || '').trim() !== '').length >= 2);
          if (bestHeaderRowIdx === -1) bestHeaderRowIdx = 0;
        }

        const headerRowIdx = bestHeaderRowIdx;
        const startRowIdx = headerRowIdx + 1;

        let categoryIdx = -1;
        let codeIdx = -1;
        let nameIdx = -1;
        let imageIdx = -1;

        const headerRow = (rows[headerRowIdx] || []).map(c => String(c).trim());
        headerRow.forEach((colName, idx) => {
          const norm = stripAccents(colName);
          if (['kategori', 'category', 'grup', 'tur', 'katalog', 'urungrubu', 'kategoriadi', 'kat'].includes(norm)) categoryIdx = idx;
          if (['kod', 'code', 'urunkodu', 'productcode', 'malzemekodu', 'stokkodu', 'artno', 'referans', 'itemcode', 'modelkodu', 'parcakodu', 'sipariskodu', 'sku'].includes(norm)) codeIdx = idx;
          if (['isim', 'name', 'urunadi', 'productname', 'aciklama', 'description', 'tanim', 'uruntanimi', 'urunismi', 'detay', 'modeladi', 'urun'].includes(norm)) nameIdx = idx;
          if (['gorsel', 'image', 'resim', 'gorselurl', 'imageurl', 'fotograf', 'photo', 'picture', 'gorseller', 'resimurl'].includes(norm)) imageIdx = idx;
        });

        const sampleRows = rows.slice(startRowIdx, Math.min(rows.length, startRowIdx + 15));
        const numCols = Math.max(headerRow.length, ...sampleRows.map(r => Array.isArray(r) ? r.length : 0));

        if (codeIdx === -1 || nameIdx === -1 || imageIdx === -1) {
          const colStats: { col: number; isImageUrl: boolean; isLongText: boolean; isUniqueCode: boolean; uniqueCount: number }[] = [];
          for (let c = 0; c < numCols; c++) {
            let isImageUrl = false;
            let isLongText = false;
            let isUniqueCode = false;
            const values = sampleRows.map(r => String(r[c] || '').trim()).filter(Boolean);
            const uniqueVals = new Set(values);

            values.forEach(val => {
              if (val.startsWith('http://') || val.startsWith('https://')) {
                if (val.includes('Product_Shots') || val.includes('.webp') || val.includes('.jpg') || val.includes('.png') || val.includes('THUMB') || val.includes('bynder') || val.includes('assets') || val.includes('media')) {
                  isImageUrl = true;
                }
              } else if (val.length > 20 && val.includes(' ')) {
                isLongText = true;
              } else if (val.length >= 3 && val.length <= 30 && /^[A-Z0-9_-]+$/i.test(val) && /\d/.test(val)) {
                isUniqueCode = true;
              }
            });

            colStats.push({ col: c, isImageUrl, isLongText, isUniqueCode, uniqueCount: uniqueVals.size });
          }

          if (imageIdx === -1) {
            const imgCol = colStats.find(s => s.isImageUrl);
            if (imgCol) imageIdx = imgCol.col;
          }
          if (nameIdx === -1) {
            const nameCol = colStats.find(s => s.isLongText);
            if (nameCol) nameIdx = nameCol.col;
          }
          if (codeIdx === -1) {
            const codeCol = colStats.filter(s => s.isUniqueCode && s.col !== imageIdx && s.col !== nameIdx)
              .sort((a, b) => b.uniqueCount - a.uniqueCount)[0];
            if (codeCol) codeIdx = codeCol.col;
          }
        }

        if (codeIdx === -1) codeIdx = 0;
        if (nameIdx === -1) nameIdx = codeIdx === 0 ? 1 : 0;

        const imported: CatalogItem[] = [];
        let imageExtractedCount = 0;
        let lastValidCategory = 'Aksesuar';

        const dataRows = rows.slice(startRowIdx);
        dataRows.forEach((row: any, idx: number) => {
          if (!Array.isArray(row)) return;
          const rawCat = categoryIdx !== -1 ? String(row[categoryIdx] || '').trim() : '';
          if (rawCat) {
            lastValidCategory = normalizeCategory(rawCat);
          }
          const category = lastValidCategory;

          const code = String(row[codeIdx] || '').trim();
          const name = String(row[nameIdx] || '').trim();
          let image = imageIdx !== -1 ? String(row[imageIdx] || '').trim() : null;

          const excelRowNum = idx + startRowIdx + 1;

          if (!image || (!image.startsWith('http://') && !image.startsWith('https://') && !image.startsWith('data:'))) {
            if (byRow[excelRowNum]) {
              image = byRow[excelRowNum];
              imageExtractedCount++;
            } else if (mediaList[idx]) {
              image = mediaList[idx];
              imageExtractedCount++;
            } else {
              image = null;
            }
          } else {
            imageExtractedCount++;
          }

          if (!code || !name) return;
          imported.push({ category, code, name, image: image || null });
        });

        if (imported.length === 0) {
          alert('Dosyadan içe aktarılabilecek geçerli ürün bulunamadı. Lütfen Excel dosyasında Kod ve İsim sütunlarının doluluğunu kontrol edin.');
          setIsUploadingExcel(false);
          return;
        }

        // Compress Extracted Base64 Images in Throttled Batches (Chunks of 5)
        const droppedImageCodes: string[] = [];
        const CONCURRENCY = 5;

        for (let i = 0; i < imported.length; i += CONCURRENCY) {
          const chunk = imported.slice(i, i + CONCURRENCY);
          await Promise.all(
            chunk.map(async (item) => {
              if (item.image && item.image.startsWith('data:image')) {
                try {
                  const compressed = await compressBase64Image(item.image, 800, 0.85);
                  if (compressed && compressed.length > 0) {
                    item.image = compressed;
                  } else {
                    droppedImageCodes.push(item.code);
                    item.image = '';
                  }
                } catch (e) {
                  console.warn(`Image compression failed for ${item.code}:`, e);
                  droppedImageCodes.push(item.code);
                  item.image = '';
                }
              }
            })
          );
        }

        const existingCodes = new Set(catalog.map(c => c.code.trim().toUpperCase()));
        let newCount = 0;
        let updatedCount = 0;
        imported.forEach(item => {
          if (existingCodes.has(item.code.trim().toUpperCase())) {
            updatedCount++;
          } else {
            newCount++;
          }
        });

        if (updatedCount > 0) {
          const confirmMsg = `Excel dosyasında ${newCount} yeni ürün ve ${updatedCount} adet mevcut ürün tespit edildi.\n\nMevcut ${updatedCount} ürünün bilgilerinin Excel verisiyle güncellenmesini onaylıyor musunuz?`;
          if (!window.confirm(confirmMsg)) {
            setIsUploadingExcel(false);
            e.target.value = '';
            return;
          }
        }

        // Excel içe aktarma sadece EKLER/GÜNCELLER, hiçbir şeyi silmez — bu yüzden
        // tüm koleksiyonu tekrar yazmaya değil, sadece dosyadaki ürünleri yazmaya gerek var.
        const itemsToUpsert: CatalogItem[] = imported.map(item => {
          const key = item.code.trim().toUpperCase();
          const existing = catalog.find(c => c.code.trim().toUpperCase() === key);
          return { ...item, image: item.image || existing?.image || '' };
        });
        await upsertCatalogItems(itemsToUpsert);

        const firstCategory = imported[0]?.category || 'Aksesuar';
        setCatalogSearch('');
        setSelectedCatalogCategory(firstCategory);
        setActiveSpecCategory(firstCategory);

        const droppedNotice = droppedImageCodes.length > 0
          ? `\n\n⚠️ Dikkat: ${droppedImageCodes.length} adet ürünün görseli aşırı büyük olduğu için otomatik atlandı, ancak ürünler katalog listenize eklendi.`
          : '';

        alert(`✅ ${imported.length} adet ürün${imageExtractedCount > 0 ? ` (${imageExtractedCount - droppedImageCodes.length} adedi görselli)` : ''} Excel'den başarıyla içe aktarıldı ve kataloğa eklendi!${droppedNotice}\n\nOtomatik Aktarılan Kategori: "${firstCategory}" sekmesi seçildi.`);
      } catch (err) {
        console.error('Excel import error:', err);
        alert('Excel dosyası okunurken hata oluştu: ' + (err as Error).message);
      } finally {
        setIsUploadingExcel(false);
        e.target.value = '';
      }
    };
    reader.onerror = (err) => {
      console.error('FileReader error:', err);
      alert('Dosya okunurken bir hata oluştu.');
      setIsUploadingExcel(false);
      e.target.value = '';
    };
  };

  const handleCatalogItemImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressedImage = await compressImageFile(file, 800, 0.85);
      setCatalogForm(prev => ({ ...prev, image: compressedImage }));
    } catch (err) {
      console.error('Image compression failed:', err);
      alert('Görsel işlenirken hata oluştu: ' + (err as Error).message);
    }
    e.target.value = '';
  };

  const handleCatalogBrowserImageUpload = async (code: string, file: File) => {
    try {
      const compressedImage = await compressImageFile(file, 800, 0.85);
      const codeUpper = code.trim().toUpperCase();

      const existingItem = catalog.find(cat => cat.code.trim().toUpperCase() === codeUpper);
      if (!existingItem) return;

      await upsertCatalogItems([{ ...existingItem, image: compressedImage }]);

      setSpecItems(prev => prev.map(spec =>
        spec.code.trim().toUpperCase() === codeUpper
          ? { ...spec, image: compressedImage }
          : spec
      ));
    } catch (err: any) {
      console.error('Catalog browser image upload failed:', err);
      alert('Görsel yüklenirken hata oluştu: ' + err.message);
    }
  };

  // Matris sekmesindeki bir renk için görsel yükler: katalogda aynı adlı `Renk` öğesi varsa
  // görselini günceller, yoksa o adla yeni bir `Renk` öğesi oluşturur (katmalı, mevcut kayıtlara dokunmaz).
  const handleMatrixColorImageUpload = async (colorName: string, file: File) => {
    try {
      const compressedImage = await compressImageFile(file, 800, 0.85);
      const item = buildColorCatalogItem(colorName, compressedImage, catalog);
      await upsertCatalogItems([item]);

      const codeUpper = item.code.trim().toUpperCase();
      setSpecItems(prev => prev.map(spec =>
        (spec.category === 'Renk' || spec.category === 'Kapak Rengi') && spec.code.trim().toUpperCase() === codeUpper
          ? { ...spec, image: compressedImage }
          : spec
      ));
    } catch (err: any) {
      console.error('Matrix color image upload failed:', err);
      alert('Görsel yüklenirken hata oluştu: ' + err.message);
    }
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  const handleDismissBanner = () => {
    setIsBannerDismissed(true);
    localStorage.setItem('pwa_install_banner_dismissed_date', new Date().toDateString());
  };

  const handleUpdatePriceRow = (key: string, fields: Partial<PricingRow>) => {
    effectiveSetPricingRows(effectivePricingRows.map(row => row.key === key ? { ...row, ...fields } : row));
  };

  const handleAddManualPriceRow = () => {
    if (effectivePricingRows.filter(r => r.isManual).length >= MAX_MANUAL_ROWS) return;
    effectiveSetPricingRows([
      ...effectivePricingRows,
      { key: `manual_${crypto.randomUUID()}`, categoryName: '', listPrice: '', campaignDiscount: '', extraDiscount: '', vatRate: 0.20, isManual: true },
    ]);
  };

  const handleRemovePriceRow = (key: string) => {
    effectiveSetPricingRows(effectivePricingRows.filter(row => !(row.isManual && row.key === key)));
  };

  const handleListPriceChange = (key: string, rawValue: string) => {
    const digits = rawValue.replace(/\D/g, '');
    handleUpdatePriceRow(key, { listPrice: digits === '' ? '' : (parseInt(digits, 10) || '') });
  };

  const handleAddSpecItemFromCatalog = (catalogItem: CatalogItem) => {
    effectiveSetSpecItems(prev => [...prev, { id: crypto.randomUUID(), category: catalogItem.category, code: catalogItem.code, name: catalogItem.name, image: catalogItem.image || null }]);
    setSearchQuery(''); setShowSearchResults(false);
  };

  const handleAddCustomSpecItem = () => {
    effectiveSetSpecItems(prev => [...prev, { id: crypto.randomUUID(), category: activeSpecCategory, code: 'ÖZEL_KOD', name: 'Yeni Özel Tasarım Ürünü', image: null }]);
  };

  const handleUpdateSpecItem = (id: string, fields: Partial<SpecItem>) => {
    effectiveSetSpecItems(prev => prev.map(item => item.id === id ? { ...item, ...fields } : item));
  };

  const handleRemoveSpecItem = (id: string) => effectiveSetSpecItems(prev => prev.filter(item => item.id !== id));

  const handleTriggerUpload = (itemId: string) => {
    setUploadActiveItemId(itemId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (uploadActiveItemId) {
        try {
          const compressedBase64 = await compressImageFile(file, 800, 0.85);
          // Teklif ekranından yüklenen görsel sadece bu teklife (specItems) özel kalır;
          // ana ürün kataloğuna asla yazılmaz. Kataloğu güncellemek için Katalog
          // Yönetimi panelindeki düzenleme akışı kullanılmalıdır.
          handleUpdateSpecItem(uploadActiveItemId, { image: compressedBase64 });
        } catch (err: any) {
          console.error("Image compression failed:", err);
          const reader = new FileReader();
          reader.onload = (event) => {
            handleUpdateSpecItem(uploadActiveItemId, { image: event.target?.result as string });
          };
          reader.readAsDataURL(file);
        }
        setUploadActiveItemId(null);
      }
    }
    e.target.value = '';
  };

  const processImageFiles = (files: File[]): void => {
    if (files.length === 0) return;
    Promise.all(files.map(file =>
      compressImageFile(file, 1200, 0.75).catch(err => {
        console.error("Failed to compress:", file.name, err);
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = e => resolve(e.target?.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    )).then(urls => effectiveSetProjectImages(prev => [...prev, ...urls]))
      .catch(err => { console.error(err); alert('Görseller yüklenirken bir hata oluştu: ' + err.message); });
  };

  const handleProjectImagesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) processImageFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleProjectImagesDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const imageFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) { alert('Lütfen sadece görsel dosyaları (.png, .jpg, .jpeg, .webp) yükleyin.'); return; }
    processImageFiles(imageFiles);
  };

  const logProposalActivity = async (actionType: 'export_json' | 'print_pdf') => {
    if (!db || !currentUser) return;
    try {
      await setDoc(doc(db, 'proposal_analytics', proposalId), {
        proposalId,
        designerId: currentUser.uid,
        designerName: currentUser.displayName || currentUser.email || 'Bilinmeyen Kullanıcı',
        clientName: client.clientName || 'İsimsiz Müşteri',
        totalAmount: Math.max(0, (pricingCalculations.finalTotal || 0) - salesPointDiscount),
        elapsedSeconds,
        actionType,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (err) {
      console.error("Failed to log proposal analytics:", err);
    }
  };

  const handleNewProposal = (type: 'retail' | 'bulk' = 'retail'): boolean => {
    if (!window.confirm('Mevcut teklif verilerini temizleyip yeni bir teklif başlatmak istediğinize emin misiniz?')) {
      // Cancelling must leave everything untouched — including the current
      // screen, so callers key their navigation off this return value.
      return false;
    }

    setReadOnlyState({ isReadOnly: false });
    setClient({ ...DEFAULT_CLIENT, designer: userName || '', storeName: userAssignedSalesPoint || '', designerPhone: userPhone || '' });
    setConfig(DEFAULT_CONFIG);
    setPricingRows(DEFAULT_PRICING_ROWS);
    setSalesPointDiscount(0);
    setSpecItems([]);
    setProjectImages([]);
    setProposalId('prop_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16));
    setElapsedSeconds(0);
    localStorage.removeItem('intema_quoting_draft');

    setProposalType(type);
    setProjectMeta({ projectName: '', siteName: '' });
    if (type === 'bulk') {
      const firstUnit = createDefaultUnit('Daire Tipi 1');
      setUnits([firstUnit]);
      setActiveUnitId(firstUnit.id);
    } else {
      setUnits([]);
      setActiveUnitId('');
    }
    return true;
  };

  const exitReadOnlyMode = () => {
    setReadOnlyState({ isReadOnly: false });
    handleNewProposal();
  };

  const saveProposalToCloud = async (isAutoSave: boolean = false) => {
    if (!db) return;
    if (!currentUser) {
      if (!isAutoSave) {
        alert("Oturum bilgisi doğrulanamadı, lütfen sayfayı yenileyip tekrar deneyin.");
      }
      return;
    }
    if (readOnlyState.isReadOnly) {
      if (!isAutoSave) {
        alert("Başka bir tasarımcıya ait teklif salt okunur moddadır, kaydedilemez. Değişiklik yapmak istiyorsanız 'Klonla' seçeneği ile kendi kopyanızı oluşturabilirsiniz.");
      }
      return;
    }
    setAutoSaveStatus('saving');
    try {
      const docRef = doc(db, 'proposals', proposalId);
      const isBulk = proposalType === 'bulk';
      const totalAmount = isBulk
        ? units.reduce((sum, u) => sum + ((u.manualUnitPrice || 0) + (u.manualVat || 0)) * u.quantity, 0)
        : pricingCalculations.netGrandTotal;
      const displayName = isBulk ? (projectMeta.projectName || 'İsimsiz Proje') : (client.clientName || 'İsimsiz Müşteri');

      const dataToSave = {
        proposalId,
        designerId: currentUser.uid,
        designerName: currentUser.displayName || currentUser.email || 'Bilinmeyen Kullanıcı',
        clientName: displayName,
        status: 'draft',
        totalAmount,
        elapsedSeconds,
        client,
        proposalType,
        ...(isBulk
          ? { projectMeta, units }
          : { config, pricingRows, salesPointDiscount, specItems, projectImages }),
        updatedAt: new Date().toISOString(),
        ...(isAutoSave ? {} : { createdAt: new Date().toISOString() })
      };

      // Firestore doküman limiti 1 MB (1.048.576 byte); yaklaşık boyutu kontrol edip
      // aşımda ham "size exceeds" hatası yerine anlaşılır bir uyarı ver.
      const approxSize = new Blob([JSON.stringify(dataToSave)]).size;
      if (approxSize > 1_000_000) {
        throw new Error(
          `Teklif verisi çok büyük (${(approxSize / 1024 / 1024).toFixed(2)} MB). Lütfen proje görsellerinden bazılarını kaldırıp tekrar deneyin.`
        );
      }

      await setDoc(docRef, dataToSave, { merge: true });

      if (!isAutoSave) {
        logProposalActivity('export_json');
        alert(`'${displayName}' başarıyla bulut arşivine kaydedildi!`);
      }

      setAutoSaveStatus('saved');
      setLastSavedAt(new Date());
    } catch (err: any) {
      console.error("Failed to save proposal to cloud:", err);
      setAutoSaveStatus('error');
      if (!isAutoSave) {
        alert("Teklif buluta kaydedilirken hata oluştu: " + err.message);
      }
    }
  };

  // Mark auto-save status as 'unsaved' when key proposal data changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!readOnlyState.isReadOnly && currentUser) {
      setAutoSaveStatus('unsaved');
    }
  }, [client, config, pricingRows, salesPointDiscount, specItems, projectImages, projectMeta, units]);

  // Automatic cloud saving disabled - manual save via 'Buluta Kaydet' button

  const loadProposalFromCloud = (docData: any) => {
    const isOtherUser = currentUser && docData.designerId && docData.designerId !== currentUser.uid;

    if (isOtherUser) {
      setReadOnlyState({
        isReadOnly: true,
        designerName: docData.designerName || 'Diğer Tasarımcı',
        originalDesignerId: docData.designerId,
      });
    } else {
      setReadOnlyState({ isReadOnly: false });
    }

    if (docData.proposalId) setProposalId(docData.proposalId);
    if (typeof docData.elapsedSeconds === 'number') setElapsedSeconds(docData.elapsedSeconds);
    if (docData.client) setClient(docData.client);

    const isBulk = docData.proposalType === 'bulk';
    setProposalType(isBulk ? 'bulk' : 'retail');
    if (isBulk) {
      setProjectMeta(docData.projectMeta || { projectName: '', siteName: '' });
      setUnits(docData.units || []);
      setActiveUnitId(docData.units?.[0]?.id || '');
      setSalesPointDiscount(0);
    } else {
      if (docData.config) setConfig(docData.config);
      if (docData.pricingRows) setPricingRows(docData.pricingRows);
      setSalesPointDiscount(docData.salesPointDiscount || 0);
      if (docData.specItems) setSpecItems(docData.specItems);
      if (docData.projectImages) setProjectImages(docData.projectImages);
      setUnits([]);
      setActiveUnitId('');
    }

    if (isOtherUser) {
      alert(`'${docData.clientName || 'Teklif'}' (Tasarımcı: ${docData.designerName || 'Bilinmeyen'}) Salt Okunur (İnceleme) Modunda açıldı. Değişiklik yapılamaz.`);
    } else {
      setAutoSaveStatus('saved');
      setLastSavedAt(new Date());
      alert(`'${docData.clientName || 'Teklif'}' başarıyla editöre yüklendi.`);
    }
  };

  const cloneProposalFromCloud = (docData: any) => {
    setReadOnlyState({ isReadOnly: false });
    const newId = 'prop_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16);
    setProposalId(newId);
    setElapsedSeconds(0);
    if (docData.client) {
      setClient({
        ...docData.client,
        clientName: docData.client.clientName ? `${docData.client.clientName} (Revizyon)` : 'Yeni Teklif Revizyonu'
      });
    }

    const isBulk = docData.proposalType === 'bulk';
    setProposalType(isBulk ? 'bulk' : 'retail');
    if (isBulk) {
      setProjectMeta({
        ...(docData.projectMeta || { projectName: '', siteName: '' }),
        projectName: docData.projectMeta?.projectName ? `${docData.projectMeta.projectName} (Revizyon)` : 'Yeni Proje Revizyonu',
      });
      const clonedUnits = (docData.units || []).map((u: KitchenUnit) => ({ ...u, id: 'unit_' + crypto.randomUUID().replace(/-/g, '').substring(0, 12) }));
      setUnits(clonedUnits);
      setActiveUnitId(clonedUnits[0]?.id || '');
      setSalesPointDiscount(0);
    } else {
      if (docData.config) setConfig(docData.config);
      if (docData.pricingRows) setPricingRows(docData.pricingRows);
      setSalesPointDiscount(docData.salesPointDiscount || 0);
      if (docData.specItems) setSpecItems(docData.specItems);
      if (docData.projectImages) setProjectImages(docData.projectImages);
      setUnits([]);
      setActiveUnitId('');
    }
    setAutoSaveStatus('unsaved');
    alert(`Teklif kopyalandı ve yeni revizyon olarak editöre yüklendi.`);
  };

  const handleExportDraft = () => {
    logProposalActivity('export_json');
    const draftData = proposalType === 'bulk'
      ? { version: '1.0', proposalType: 'bulk' as const, proposalId, elapsedSeconds, client, projectMeta, units }
      : { version: '1.0', proposalId, elapsedSeconds, client, config, pricingRows, salesPointDiscount, specItems, projectImages };
    const blob = new Blob([JSON.stringify(draftData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const name = client.clientName ? client.clientName.trim().replace(/[^a-zA-Z0-9şŞçÇğĞıİöÖüÜ\s]/g, '').replace(/\s+/g, '_') : 'Yeni_Teklif';
    link.href = url;
    link.download = `Intema_Teklif_Draft_${name}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleTriggerImport = () => jsonInputRef.current?.click();
  const handleTriggerXmlImport = () => xmlInputRef.current?.click();

  const applyParsedXmlProject = (parsed: ParsedXmlProject) => {
    // Müşteri Adı, Mutfak Modeli, Kapak Malzemesi, Kapak Rengi, Gövde Rengi, Cam Tipi ve Profil Rengi XML'den
    // otomatik çekilmez; kullanıcı elle girer/seçer (mevcut değerler korunur). XML'den yalnızca katalogla eşleşen ürünler alınır.
    setSpecItems(parsed.items);

    alert(`XML Projesi Başarıyla İşlendi!\n\n` +
      `• Eklenen Ürün Sayısı: ${parsed.items.length} Adet`
    );
  };

  const handleImportXmlProject = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const xmlText = event.target?.result as string;
          const parsed = parseXmlProject(xmlText, file.name, catalog, MODELS, BODY_COLORS, false);

          if (parsed.unmatchedAccessories && parsed.unmatchedAccessories.length > 0) {
            setPendingXmlImport({ xmlText, fileName: file.name, parsed });
          } else {
            applyParsedXmlProject(parsed);
          }
        } catch (err) {
          alert('XML dosyası okunurken hata oluştu: ' + (err as Error).message);
        }
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  const handleConfirmXmlImport = (includeUnmatched: boolean) => {
    if (!pendingXmlImport) return;
    const { xmlText, fileName } = pendingXmlImport;
    const finalParsed = parseXmlProject(xmlText, fileName, catalog, MODELS, BODY_COLORS, includeUnmatched);
    applyParsedXmlProject(finalParsed);
    setPendingXmlImport(null);
  };

  const handleImportDraft = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const parsed = JSON.parse(event.target?.result as string);
          if (parsed?.proposalType === 'bulk' && parsed?.client && parsed?.units) {
            setProposalType('bulk');
            setClient(parsed.client);
            setProjectMeta(parsed.projectMeta || { projectName: '', siteName: '' });
            setUnits(parsed.units);
            setActiveUnitId(parsed.units[0]?.id || '');
            setSalesPointDiscount(0);
            setProposalId(parsed.proposalId || ('prop_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16)));
            setElapsedSeconds(parsed.elapsedSeconds || 0);
            alert('Toplu proje teklifi başarıyla yüklendi!');
          } else if (parsed?.client && parsed?.config && parsed?.pricingRows && parsed?.specItems) {
            setProposalType('retail');
            setClient(parsed.client);
            setConfig(parsed.config);
            setPricingRows(migratePricingRows(parsed.pricingRows));
            setSalesPointDiscount(parsed.salesPointDiscount || 0);
            setSpecItems(healSpecItems(parsed.specItems));
            setProjectImages(parsed.projectImages || []);
            setProposalId(parsed.proposalId || ('prop_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16)));
            setElapsedSeconds(parsed.elapsedSeconds || 0);
            alert('Teklif başarıyla yüklendi!');
          } else {
            alert('Hata: Geçersiz dosya formatı.');
          }
        } catch (err) { alert('Dosya okunurken hata: ' + (err as Error).message); }
      };
      reader.readAsText(file);
    }
    e.target.value = '';
  };

  const handleDragStart = (index: number) => setDraggedIndex(index);

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newItems = [...effectiveSpecItems];
    const dragged = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, dragged);
    setDraggedIndex(index);
    effectiveSetSpecItems(newItems);
  };

  const handleDragEnd = () => setDraggedIndex(null);

  // ── Context Value ─────────────────────────────────────────────────────────────

  const value: AppContextValue = {
    mode, setMode,
    editTab, setEditTab,
    fileInputRef, jsonInputRef, projectImageInputRef, catalogImportRef, excelImportRef, fileMenuRef,
    isUploadingExcel, handleDownloadExcelTemplate,
    fileMenuOpen, setFileMenuOpen,
    uploadActiveItemId,
    catalog, isAdminMode, isSidebarExpanded, setIsSidebarExpanded, catalogSearch, setCatalogSearch, selectedCatalogCategory, setSelectedCatalogCategory,
    proposalId, elapsedSeconds, isCatalogModalOpen, setIsCatalogModalOpen,
    isProposalsModalOpen, setIsProposalsModalOpen, readOnlyState, exitReadOnlyMode, saveProposalToCloud, loadProposalFromCloud, cloneProposalFromCloud,
    autoSaveStatus, lastSavedAt,
    logProposalActivity,
    catalogActiveTab, setCatalogActiveTab,
    isEditingCatalogItem, setIsEditingCatalogItem, editingItemCode, setEditingItemCode,
    catalogForm, setCatalogForm,
    compatibilityRules,
    selectedMatrixModel, setSelectedMatrixModel, selectedMatrixMaterial, setSelectedMatrixMaterial,
    newMatrixModelName, setNewMatrixModelName, newMatrixMaterialName, setNewMatrixMaterialName,
    newMatrixColorName, setNewMatrixColorName,
    deferredPrompt, isStandalone, isBannerDismissed, showIOSGuide, setShowIOSGuide, pwaInstalled, isIOS, isSafari,
    client, setClient, config: effectiveConfig, setConfig: effectiveSetConfig,
    pricingRows: effectivePricingRows, setPricingRows: effectiveSetPricingRows,
    salesPointDiscount, setSalesPointDiscount,
    specItems: effectiveSpecItems, setSpecItems: effectiveSetSpecItems,
    projectImages: effectiveProjectImages, setProjectImages: effectiveSetProjectImages,
    draggedIndex, isDragging, setIsDragging,
    proposalType, projectMeta, setProjectMeta, units, activeUnitId, setActiveUnitId,
    addUnit, removeUnit, renameUnit, setUnitQuantity, setUnitManualPricing,
    searchQuery, setSearchQuery, showSearchResults, setShowSearchResults, activeSpecCategory, setActiveSpecCategory,
    MODELS, availableMaterials, getAvailableMaterialsForIndex, availableColors, getAvailableColorsForIndex, resolvedModelName, resolvedMaterialName, resolvedDoorColor, resolvedBodyColor, resolvedGlassType, resolvedProfileColor,
    filteredCatalog, browsableCatalog, allVisualAttachmentItems,
    pricingCalculations, ...previewPricing, selectedStore, catalogColorNames, salesPoints,
    handleExitAdminMode, saveCompatibilityRules, saveCatalog, upsertCatalogItems, deleteCatalogItemsByCode,
    handleSaveCatalogItem, handleEditCatalogItemClick, handleDeleteCatalogItem,
    handleResetCatalogToDefault, handleResetMatrixToDefault, handleExportCatalog, handleExportCatalogExcel, handleImportCatalog, handleImportExcel,
    handleCatalogItemImageChange, handleCatalogBrowserImageUpload, handleMatrixColorImageUpload, handleInstallClick, handleDismissBanner,
    handleUpdatePriceRow, handleListPriceChange, handleAddManualPriceRow, handleRemovePriceRow,
    handleAddSpecItemFromCatalog, handleAddCustomSpecItem, handleUpdateSpecItem, handleRemoveSpecItem,
    handleTriggerUpload, handleFileChange, handleProjectImagesUpload, handleProjectImagesDrop,
    handleNewProposal, handleExportDraft, handleTriggerImport, handleImportDraft, handleTriggerXmlImport, handleImportXmlProject, xmlInputRef,
    handleDragStart, handleDragOver, handleDragEnd,
    currentUser, userName, userRole, userAssignedSalesPoint, userPhone, loadingAuth, logout,
    formatPhoneNumber,
    pendingXmlImport, handleConfirmXmlImport,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      <input
        type="file"
        ref={xmlInputRef}
        onChange={handleImportXmlProject}
        accept=".xml"
        className="hidden"
      />
    </AppContext.Provider>
  );
}
